import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Database, Maximize2, Minimize2, Moon, Sun, Pencil, RefreshCw, ShieldCheck, ListChecks, Rocket } from 'lucide-react';
import type { ValidationResult, ValidationRecord } from '../../types/rosterFlow';

// ─── Shared CSS ──────────────────────────────────────────────────────────────

const injectStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('val-modal-styles')) {
    const el = document.createElement('style');
    el.id = 'val-modal-styles';
    el.textContent = `
      @keyframes val-fadeIn { from{opacity:0}to{opacity:1} }
      @keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
      @keyframes val-slideUp {
        from{opacity:0;transform:translateY(32px) scale(0.97)}
        to  {opacity:1;transform:translateY(0)    scale(1)   }
      }
      .val-tab-btn{ background:none;border:none;cursor:pointer;transition:all 0.2s; }
      .val-tr:hover td{ background:rgba(0,38,119,0.025); }
      .val-err-cell input:focus{ outline:none; }
    `;
    document.head.appendChild(el);
  }
};

// ─── API error_field → roster column key mapping ─────────────────────────────

const ERROR_FIELD_TO_COL: Record<string, string> = {
  'practitioner.provider_first_name':  'FIRST_NAME',
  'practitioner.provider_last_name':   'LAST_NAME',
  'practitioner.npi':                  'NPI',
  'practitioner.primary_degree':       'PRIMARY_DEGREE',
  'practitioner.primary_speciality':   'PRIMARY_SPECIALTY',
  'tax_id.tax_id':                     'TIN',
  'tax_id.address_pairs[0].provider_billing_address.address_1':            'BILLING_ADDRESS_1',
  'tax_id.address_pairs[0].provider_billing_address.city':                 'BILLING_CITY',
  'tax_id.address_pairs[0].provider_billing_address.state':                'BILLING_STATE',
  'tax_id.address_pairs[0].provider_billing_address.billing_phone_number': 'BILLING_PHONE_NUMBER',
  'tax_id.address_pairs[0].provider_billing_address.zip':                  'BILLING_ZIP',
  'tax_id.address_pairs[0].provider_service_address.address_1':            'ADDRESS_1',
  'tax_id.address_pairs[0].provider_service_address.city':                 'CITY',
  'tax_id.address_pairs[0].provider_service_address.state':                'STATE',
  'tax_id.address_pairs[0].provider_service_address.zip':                  'ZIP_CODE',
  'tax_id.address_pairs[0].provider_service_address.primary_telephone':    'PHONE',
};

/** Human-readable label for each error_field path. */
const ERROR_FIELD_LABEL: Record<string, string> = {
  'practitioner.provider_first_name':  'First Name',
  'practitioner.provider_last_name':   'Last Name',
  'practitioner.npi':                  'NPI',
  'practitioner.primary_degree':       'Primary Degree',
  'practitioner.primary_speciality':   'Primary Specialty',
  'tax_id.tax_id':                     'Tax ID (TIN)',
  'tax_id.address_pairs[0].provider_billing_address.address_1':            'Billing Address 1',
  'tax_id.address_pairs[0].provider_billing_address.city':                 'Billing City',
  'tax_id.address_pairs[0].provider_billing_address.state':                'Billing State',
  'tax_id.address_pairs[0].provider_billing_address.billing_phone_number': 'Billing Phone',
  'tax_id.address_pairs[0].provider_billing_address.zip':                  'Billing ZIP',
  'tax_id.address_pairs[0].provider_service_address.address_1':            'Service Address 1',
  'tax_id.address_pairs[0].provider_service_address.city':                 'Service City',
  'tax_id.address_pairs[0].provider_service_address.state':                'Service State',
  'tax_id.address_pairs[0].provider_service_address.zip':                  'Service ZIP',
  'tax_id.address_pairs[0].provider_service_address.primary_telephone':    'Primary Phone',
};

/** Resolve a field path to its human-readable label, falling back to the col key or the raw path. */
function fieldLabel(field: string): string {
  if (ERROR_FIELD_LABEL[field]) return ERROR_FIELD_LABEL[field];
  const col = ERROR_FIELD_TO_COL[field];
  if (col) return col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  // Last-resort: prettify the tail segment of the path
  const tail = field.split('.').pop() ?? field;
  return tail.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Build a col → error-message map for one record. */
function buildColErrorMap(rec: ValidationRecord): Record<string, string> {
  const map: Record<string, string> = {};
  for (const iss of rec.issues) {
    const col = ERROR_FIELD_TO_COL[iss.field];
    if (col) map[col] = iss.message;
  }
  return map;
}

// ─── Editable cell with hover tooltip ────────────────────────────────────────

interface EditableCellProps {
  value: string;
  errorMsg?: string;
  isEdited?: boolean;
  cellBg: string;
  cellColor: string;
  borderBase: string;
  onChange: (v: string) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({ value, errorMsg, isEdited = false, cellBg, cellColor, borderBase, onChange }) => {
  const [hovered, setHovered] = useState(false);
  const hasError = !!errorMsg;
  // A cell is "corrected" when it has an error AND the user has typed a non-empty value
  const isCorrected = hasError && isEdited && value.trim() !== '';
  // Priority: corrected (green) > uncorrected error (red) > edited no-error (green) > neutral
  const borderColor = isCorrected ? '#3b82f6' : hasError ? '#ef4444' : isEdited ? '#3b82f6' : borderBase;
  const bgColor     = isCorrected ? 'rgba(59,130,246,0.10)' : hasError ? 'rgba(239,68,68,0.08)' : isEdited ? 'rgba(59,130,246,0.08)' : cellBg;
  const textColor   = isCorrected ? '#1d4ed8' : hasError ? '#b91c1c' : isEdited ? '#1d4ed8' : cellColor;
  // Only show tooltip while error and NOT yet corrected
  const showTooltip = hasError && !isCorrected && hovered;
  return (
    <div
      className={hasError ? 'val-err-cell' : undefined}
      style={{ position: 'relative', minWidth: 90 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '4px 7px',
          borderRadius: 6,
          border: `2px solid ${borderColor}`,
          background: bgColor,
          color: textColor,
          fontSize: 12,
          fontFamily: 'inherit',
          transition: 'border-color 0.15s, background 0.15s',
          boxSizing: 'border-box',
        }}
      />
      {showTooltip && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: 0,
          background: '#1e293b', color: '#fca5a5',
          fontSize: 11, fontWeight: 600, padding: '5px 9px', borderRadius: 7,
          whiteSpace: 'nowrap', zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          border: '1px solid rgba(239,68,68,0.4)',
          pointerEvents: 'none',
        }}>
          <AlertCircle size={10} style={{ marginRight: 4, display: 'inline', verticalAlign: 'middle' }} />
          {errorMsg}
          {/* tooltip arrow */}
          <div style={{
            position: 'absolute', top: '100%', left: 12,
            borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
            borderTop: '6px solid #1e293b',
          }} />
        </div>
      )}
    </div>
  );
};

// ─── Step Indicator (shared look) ────────────────────────────────────────────

const FLOW_STEPS = ['Column Mapping', 'Validation', 'Load'];

const StepIndicator: React.FC<{ current: number; completedSteps?: boolean[]; onNavigate?: (step: number) => void }> = ({ current, completedSteps = [], onNavigate }) => (
  <div style={{ display:'flex', alignItems:'center' }}>
    {FLOW_STEPS.map((label, i) => {
      const isDone = completedSteps[i] === true;
      const active = i === current;
      const canNav = isDone && !active && !!onNavigate;
      return (
        <React.Fragment key={i}>
          <div
            onClick={canNav ? () => onNavigate!(i) : undefined}
            title={canNav ? `Go to ${label}` : undefined}
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor: canNav ? 'pointer' : 'default' }}
          >
            <div style={{
              width:26, height:26, borderRadius:'50%',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700,
              background: active ? 'white' : isDone ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.2)',
              color: active || isDone ? '#002677' : 'rgba(255,255,255,0.6)',
              boxShadow: active ? '0 0 0 3px rgba(255,255,255,0.3)' : canNav ? '0 0 0 2px rgba(255,255,255,0.4)' : 'none',
            }}>
              {i < current ? <CheckCircle size={13} color="#002677" /> : i + 1}
            </div>
            <span style={{ fontSize:10, fontWeight: active ? 700 : isDone ? 600 : 400, color: active ? 'white' : isDone ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>{label}</span>
          </div>
          {i < FLOW_STEPS.length - 1 && (
            <div style={{ width:36, height:2, background: completedSteps[i] ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)', margin:'0 4px 13px' }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Row status badge ────────────────────────────────────────────────────────

const RowBadge: React.FC<{ status: ValidationRecord['status'] }> = ({ status }) => {
  if (status === 'error') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, background:'#fee2e2', color:'#b91c1c', fontSize:11, fontWeight:700 }}>
      <AlertCircle size={10} /> Error
    </span>
  );
  if (status === 'warning') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, background:'#fef3c7', color:'#92400e', fontSize:11, fontWeight:700 }}>
      <AlertTriangle size={10} /> Warning
    </span>
  );
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, background:'#dbeafe', color:'#1d4ed8', fontSize:11, fontWeight:700 }}>
      <CheckCircle size={10} /> Valid
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export interface ValidationModalProps {
  isOpen: boolean;
  data: ValidationResult | null;
  fileName: string;
  onConfirm: () => void;
  onClose: () => void;
  /** Called when user clicks Re-validate after correcting errors. Receives the edited cell map. */
  onRevalidate?: (correctedData: Record<number, Record<string, string>>) => void;
  /** Shows a spinner on the Re-validate button while the API call is in flight. */
  isRevalidating?: boolean;
  /** When true the Load NDB button is disabled (already submitted). */
  submitted?: boolean;
  /** [columnMapping, validation, load] — true means that stage has data and can be navigated to. */
  completedSteps?: boolean[];
  /** Called when the user clicks a completed stage in the step indicator to navigate there. */
  onNavigate?: (step: number) => void;
  /** Persisted edited cell values — lifted to parent so they survive close/reopen. */
  editedData?: Record<number, Record<string, string>>;
  /** Called whenever the user edits a cell, so the parent can persist the state. */
  onEditedDataChange?: (data: Record<number, Record<string, string>>) => void;
}

export const ValidationModal: React.FC<ValidationModalProps> = ({
  isOpen, data, fileName, onConfirm, onClose, onRevalidate, isRevalidating = false, submitted = false, completedSteps = [], onNavigate,
  editedData: editedDataProp, onEditedDataChange,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'errors'>('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  // Use lifted state from parent if provided, otherwise fall back to local state
  const [localEditedData, setLocalEditedData] = useState<Record<number, Record<string, string>>>({});
  const editedData = editedDataProp ?? localEditedData;
  const setEditedData = (updater: (prev: Record<number, Record<string, string>>) => Record<number, Record<string, string>>) => {
    const next = updater(editedData);
    if (onEditedDataChange) onEditedDataChange(next);
    else setLocalEditedData(next);
  };

  useEffect(() => { injectStyles(); }, []);
  // Only reset tab on open; do NOT reset editedData (parent preserves it)
  useEffect(() => { if (isOpen) { setActiveTab(data?.errorRows ? 'errors' : 'all'); } }, [isOpen, data]);

  if (!isOpen || !data) return null;

  const { totalRows, validRows, errorRows, warningRows, records } = data;
  const hasHardErrors = errorRows > 0;

  const t = isDark ? {
    bodyBg:       'linear-gradient(145deg,#0f172a 0%,#1a2744 100%)',
    theadBg:      '#0f172a', theadColor:'#93c5fd', theadBorder:'#334155',
    rowBorder:    '#1e293b', rowNumColor:'#475569',
    cellColor:    '#cbd5e1', cellBg: '#1e293b',
    tabBarBg:     '#0f172a', tabBorder:  '#334155',
    activeTabBg:  'rgba(147,197,253,0.08)', activeTabColor:'#93c5fd', activeTabBorderColor:'#93c5fd',
    inactiveTabColor:'#64748b',
    badgeBg:      '#334155', badgeColor: '#94a3b8',
    activeBadgeBg:'#1e3a5f', activeBadgeColor:'#93c5fd',
    footerBg:     '#0f172a', footerBorder:'#334155', footerText:'#94a3b8',
    cancelBg:     '#1e293b', cancelBorder:'#475569', cancelColor:'#e2e8f0',
    issueFg:      '#f87171', warnFg: '#fbbf24',
    inputBorder:  '#334155',
  } : {
    bodyBg:       'linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)',
    theadBg:      '#f1f5f9', theadColor:'#1e40af', theadBorder:'#e5e7eb',
    rowBorder:    '#f1f5f9', rowNumColor:'#94a3b8',
    cellColor:    '#4b5563', cellBg: 'white',
    tabBarBg:     '#fafbfc', tabBorder:  '#f1f5f9',
    activeTabBg:  'rgba(0,38,119,0.05)', activeTabColor:'#002677', activeTabBorderColor:'#002677',
    inactiveTabColor:'#64748b',
    badgeBg:      '#e5e7eb', badgeColor: '#64748b',
    activeBadgeBg:'#002677', activeBadgeColor:'white',
    footerBg:     '#f9fafb', footerBorder:'#e5e7eb', footerText:'#64748b',
    cancelBg:     'white',   cancelBorder:'#e5e7eb', cancelColor:'#374151',
    issueFg:      '#b91c1c', warnFg: '#92400e',
    inputBorder:  '#e5e7eb',
  };

  const errorRecords = records.filter(r => r.status === 'error');
  const filteredRecords = activeTab === 'errors' ? errorRecords : records;

  /** True only when every error field across every error record has a non-empty value in editedData. */
  const allErrorsAddressed = errorRecords.length > 0 && errorRecords.every(rec => {
    const colErrMap = buildColErrorMap(rec);
    return Object.keys(colErrMap).every(col => {
      const val = editedData[rec.rowNumber]?.[col];
      return val !== undefined && val.trim() !== '';
    });
  });

  // All cols used in the Errors (editable) tab
  const allCols = records.length > 0 ? Object.keys(records[0].data) : [];

  const tabs = [
    { key: 'all',    label: 'All Records', count: totalRows },
    { key: 'errors', label: 'Errors',      count: errorRows },
  ] as const;

  const getCellValue = (rec: ValidationRecord, col: string) =>
    editedData[rec.rowNumber]?.[col] ?? rec.data[col] ?? '';

  const setCellValue = (rowNumber: number, col: string, val: string) =>
    setEditedData(prev => ({
      ...prev,
      [rowNumber]: { ...(prev[rowNumber] ?? {}), [col]: val },
    }));

  return (
    <div style={{
      position:'fixed', inset:0,
      background:'linear-gradient(135deg,rgba(15,23,42,0.92) 0%,rgba(30,41,59,0.88) 100%)',
      backdropFilter:'blur(16px) saturate(180%)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:999999, padding: isFullscreen ? 0 : 20,
      animation:'val-fadeIn 0.25s ease',
    }}>
      <div style={{
        background: t.bodyBg,
        borderRadius: isFullscreen ? 0 : 24, overflow:'hidden',
        display:'flex', flexDirection:'column',
        width: isFullscreen ? '100vw' : '96vw',
        maxWidth: isFullscreen ? '100vw' : 1280,
        height: isFullscreen ? '100vh' : '92vh',
        boxShadow:'0 40px 100px rgba(15,23,42,0.5),0 0 0 1px rgba(255,255,255,0.08)',
        animation:'val-slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* ── Header ── */}
        <div style={{ background:'rgb(0,38,119)', padding:'20px 28px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:11, background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <CheckCircle size={22} color="white" />
              </div>
              <div>
                <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'white', letterSpacing:'-0.3px' }}>Data Validation</h2>
                <p style={{ margin:'3px 0 0', fontSize:12, color:'rgba(255,255,255,0.7)' }}>{fileName}</p>
              </div>
            </div>
            <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)' }}>
              <StepIndicator current={1} completedSteps={completedSteps} onNavigate={onNavigate} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button onClick={() => setIsDark(d => !d)} title={isDark ? 'Light mode' : 'Dark mode'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button onClick={onClose} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display:'flex', gap:10 }}>
            {[
              { label:'Total Rows',   value: totalRows,   color:'rgba(255,255,255,0.95)' },
              { label:'Valid',        value: validRows,   color:'rgba(255,255,255,0.95)' },
              { label:'Errors',       value: errorRows,   color: errorRows > 0 ? '#fca5a5':'rgba(255,255,255,0.95)' },
              { label:'Warnings',     value: warningRows, color: warningRows > 0 ? '#fde68a':'rgba(255,255,255,0.95)' },
            ].map(s => (
              <div key={s.label} style={{ flex:1, background:'rgba(255,255,255,0.1)', borderRadius:9, padding:'9px 13px', border:'1px solid rgba(255,255,255,0.14)' }}>
                <div style={{ fontSize:22, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginTop:3, fontWeight:500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', borderBottom:`2px solid ${t.tabBorder}`, background: t.tabBarBg, padding:'0 24px', flexShrink:0 }}>
          {tabs.map(tab => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                className="val-tab-btn"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding:'14px 20px', fontSize:14, fontWeight:600,
                  color: active ? t.activeTabColor : t.inactiveTabColor,
                  borderBottom: active ? `3px solid ${t.activeTabBorderColor}` : '3px solid transparent',
                  background: active ? t.activeTabBg : 'none',
                  borderRadius: active ? '8px 8px 0 0' : 0,
                  display:'flex', alignItems:'center', gap:8,
                }}
              >
                {tab.key === 'errors' && (
                  <Pencil size={13} style={{ opacity: 0.7 }} />
                )}
                {tab.label}
                <span style={{
                  fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:10,
                  background: active ? t.activeBadgeBg : t.badgeBg,
                  color: active ? t.activeBadgeColor : t.badgeColor,
                }}>{tab.count}</span>
                {tab.key === 'errors' && tab.count > 0 && (
                  <span style={{ fontSize:10, color: active ? t.activeTabColor : '#94a3b8', opacity:0.7, fontWeight:400 }}>
                    editable
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Table ── */}
        <div style={{ flex:1, overflowY:'auto', overflowX:'auto' }}>
          {/* ── Validations Passed Tile ── */}
          {activeTab === 'all' && !hasHardErrors && (
            <div style={{
              margin: '20px 24px 0',
              borderRadius: 16,
              background: isDark ? '#1e293b' : '#ffffff',
              border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
              borderLeft: '4px solid #22c55e',
              padding: '18px 22px',
              display: 'flex', alignItems: 'center', gap: 18,
              boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,23,42,0.07)',
            }}>
              {/* Icon circle */}
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4',
                border: isDark ? '1.5px solid rgba(34,197,94,0.25)' : '1.5px solid #bbf7d0',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Rocket size={22} color="#22c55e" strokeWidth={2} />
              </div>

              {/* Text */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', letterSpacing: '-0.2px' }}>
                    Validations Passed
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: '#22c55e', color: 'white', letterSpacing: '0.04em',
                  }}>
                    <Rocket size={9} /> ALL CLEAR
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>
                    <ListChecks size={13} color={isDark ? '#64748b' : '#94a3b8'} />
                    <strong style={{ color: isDark ? '#e2e8f0' : '#334155' }}>{validRows}</strong>&nbsp;valid row{validRows !== 1 ? 's' : ''}
                  </span>
                  {warningRows > 0 && (
                    <>
                      <span style={{ color: isDark ? '#475569' : '#cbd5e1', fontSize: 12 }}>·</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: isDark ? '#fbbf24' : '#d97706' }}>
                        <AlertTriangle size={13} />
                        <strong>{warningRows}</strong>&nbsp;warning{warningRows !== 1 ? 's' : ''}
                      </span>
                    </>
                  )}
                  <span style={{ color: isDark ? '#475569' : '#cbd5e1', fontSize: 12 }}>·</span>
                  <span style={{ fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>
                    Ready to load into NDB
                  </span>
                </div>
              </div>

              {/* Right stat */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1 }}>{totalRows}</div>
                <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', marginTop: 3 }}>total rows</div>
              </div>
            </div>
          )}

          {filteredRecords.length === 0 ? (
            <div style={{ padding:48, textAlign:'center', color: t.rowNumColor, fontSize:14 }}>
              No records to display for this filter.
            </div>
          ) : activeTab === 'errors' ? (
            /* ── Editable Errors Table ── */
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  <th style={thStyle(t)}>Row</th>
                  <th style={thStyle(t)}>Status</th>
                  {allCols.map(h => (
                    <th key={h} style={{ ...thStyle(t), whiteSpace:'nowrap' }}>{h.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(rec => {
                  const colErrMap = buildColErrorMap(rec);
                  return (
                    <tr key={rec.rowNumber} className="val-tr">
                      <td style={tdStyle(t, { fontWeight:600, width:44, color: t.rowNumColor })}>{rec.rowNumber}</td>
                      <td style={tdStyle(t)}><RowBadge status={rec.status} /></td>
                      {allCols.map(col => (
                        <td key={col} style={{ ...tdStyle(t), padding:'6px 10px' }}>
                          <EditableCell
                            value={getCellValue(rec, col)}
                            errorMsg={colErrMap[col]}
                            isEdited={editedData[rec.rowNumber]?.[col] !== undefined}
                            cellBg={t.cellBg}
                            cellColor={t.cellColor}
                            borderBase={t.inputBorder}
                            onChange={v => setCellValue(rec.rowNumber, col, v)}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            /* ── Read-only All Records Table ── */
            <div style={{ padding: '16px 24px 20px' }}>
              <table style={{ width:'100%', borderCollapse:'separate', borderSpacing: '0 4px', fontSize:13 }}>
                <thead>
                  <tr>
                    {[
                      { label: '#', icon: null },
                      { label: 'Status', icon: <ShieldCheck size={12} /> },
                      { label: 'Issues', icon: <AlertCircle size={12} /> },
                    ].map(h => (
                      <th key={h.label} style={{
                        ...thStyle(t),
                        borderRadius: h.label === '#' ? '10px 0 0 10px' : h.label === 'Issues' ? '0 10px 10px 0' : undefined,
                        display: h.label === '#' ? undefined : undefined,
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          {h.icon}{h.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((rec, idx) => {
                    const rowBg = isDark
                      ? (idx % 2 === 0 ? '#1e293b' : '#172032')
                      : (idx % 2 === 0 ? '#ffffff' : '#f8fafc');
                    return (
                      <tr key={rec.rowNumber} className="val-tr">
                        <td style={{
                          ...tdStyle(t, { fontWeight: 700, width: 48, color: t.rowNumColor }),
                          background: rowBg, borderRadius: '10px 0 0 10px',
                          borderBottom: 'none', paddingLeft: 18,
                        }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 24, height: 24, borderRadius: '50%',
                            background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                            fontSize: 11, fontWeight: 700, color: t.rowNumColor,
                          }}>{rec.rowNumber}</span>
                        </td>
                        <td style={{ ...tdStyle(t, { width: 120 }), background: rowBg, borderBottom: 'none' }}>
                          <RowBadge status={rec.status} />
                        </td>
                        <td style={{ ...tdStyle(t), background: rowBg, borderRadius: '0 10px 10px 0', borderBottom: 'none' }}>
                          {rec.issues.length === 0 ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: isDark ? '#93c5fd' : '#64748b', fontSize: 12, fontWeight: 600 }}>
                              <CheckCircle size={13} /> No issues
                            </span>
                          ) : (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                              {rec.issues.map((iss, ii) => (
                                <span key={ii} style={{
                                  display:'inline-flex', alignItems:'center', gap:5,
                                  padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:600,
                                  background: iss.severity === 'error' ? (isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)') : (isDark ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.10)'),
                                  color: iss.severity === 'error' ? t.issueFg : t.warnFg,
                                  border: `1px solid ${iss.severity === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(251,191,36,0.3)'}`,
                                }}>
                                  {iss.severity === 'error' ? <AlertCircle size={11} /> : <AlertTriangle size={11} />}
                                  <strong>{fieldLabel(iss.field)}:</strong>&nbsp;{iss.message}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding:'16px 24px', background: t.footerBg, borderTop:`1px solid ${t.footerBorder}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:13, color: t.footerText }}>
            {hasHardErrors
              ? allErrorsAddressed
                ? <span style={{ color:'#fbbf24', fontWeight:600 }}>✓ All error fields filled — click Re-validate to confirm.</span>
                : <span style={{ color:'#f87171', fontWeight:600 }}>⚠ {errorRows} error(s) found — fill in all highlighted fields to enable Re-validate.</span>
              : <span style={{ color: isDark ? '#93c5fd' : '#002677', fontWeight:600 }}>✓ Validation passed — {validRows} valid row(s) ready to load.</span>
            }
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button
              onClick={onClose}
              style={{ padding:'10px 20px', borderRadius:10, border:`2px solid ${t.cancelBorder}`, background: t.cancelBg, color: t.cancelColor, fontSize:14, fontWeight:600, cursor:'pointer', transition:'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='#93c5fd'}
              onMouseLeave={e => e.currentTarget.style.borderColor= t.cancelBorder}
            >
              Cancel
            </button>
            {hasHardErrors ? (
              <button
                onClick={() => onRevalidate?.(editedData)}
                disabled={!allErrorsAddressed || isRevalidating}
                title={!allErrorsAddressed ? 'Fill in all highlighted error fields before re-validating' : undefined}
                style={{ padding:'10px 22px', borderRadius:10, border:'none', background: (!allErrorsAddressed || isRevalidating) ? '#64748b' : 'rgb(0,38,119)', color:'white', fontSize:14, fontWeight:700, cursor: (!allErrorsAddressed || isRevalidating) ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(0,38,119,0.28)', transition:'transform 0.15s,box-shadow 0.15s', opacity: (!allErrorsAddressed || isRevalidating) ? 0.55 : 1 }}
                onMouseEnter={e => { if (allErrorsAddressed && !isRevalidating) { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(0,38,119,0.38)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(0,38,119,0.28)'; }}
              >
                <RefreshCw size={15} style={{ animation: isRevalidating ? 'spin 1s linear infinite' : 'none' }} />
                {isRevalidating ? 'Re-validating…' : 'Re-validate'}
              </button>
            ) : (
              <button
                onClick={() => !submitted && onConfirm()}
                disabled={submitted}
                style={{ padding:'11px 26px', borderRadius:12, border:'none', background: submitted ? '#94a3b8' : 'linear-gradient(135deg,#059669 0%,#047857 100%)', color:'white', fontSize:14, fontWeight:700, cursor: submitted ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:10, boxShadow: submitted ? 'none' : '0 4px 14px rgba(5,150,105,0.4)', opacity: submitted ? 0.65 : 1, letterSpacing:'0.015em', transition:'transform 0.15s,box-shadow 0.15s,filter 0.15s' }}
                onMouseEnter={e => { if (!submitted) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 22px rgba(5,150,105,0.52)'; e.currentTarget.style.filter='brightness(1.08)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow= submitted ? 'none' : '0 4px 14px rgba(5,150,105,0.4)'; e.currentTarget.style.filter='brightness(1)'; }}
              >
                {submitted ? <><CheckCircle size={16} />&nbsp;Submitted</> : <><Database size={15} /> Load NDB</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Shared table cell style helpers ─────────────────────────────────────────

function thStyle(t: any): React.CSSProperties {
  return {
    padding:'11px 14px', textAlign:'left',
    background: t.theadBg, borderBottom:`2px solid ${t.theadBorder}`,
    color: t.theadColor, fontWeight:700, fontSize:11,
    textTransform:'uppercase' as const, letterSpacing:'0.5px',
    position:'sticky' as const, top:0, zIndex:1, whiteSpace:'nowrap' as const,
  };
}

function tdStyle(t: any, extra?: React.CSSProperties): React.CSSProperties {
  return { padding:'9px 14px', borderBottom:`1px solid ${t.rowBorder}`, color: t.cellColor, ...extra };
}
